import { Injectable, isDevMode } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { FinOpsAzureServicesService } from "./finops-azure-services.service";
import {
    LoadFinOpsAzureServicesHistoricalData,
    LoadFinOpsAzureServicesChartsData,
    LoadDataTableAllSubscriptionsFinOpsAzureServices,
    LoadFinOpsAzureServicesAllSubscriptionsChartsFilteredData,
} from "./finops-azure-services.actions";
import { finalize, forkJoin } from "rxjs";
import { FinOpsAzureServicesStateModel } from "./finops-azure-services.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { PreferencesFinopsState } from "src/app/store/preferences/finops/preferences-finops.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";
import { TranslateService } from "@ngx-translate/core";
import { DateService, HistoricalDate } from "src/app/shared/service/date.service";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { FloorPipe } from "src/app/shared/pipe/floor.pipe";
import { FormatNumberPipe } from "src/app/shared/pipe/format-number.pipe";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { MetaService } from "src/app/store/meta/meta.service";
import { DataForDatatableCompute, FinOpsAzureServicesHelper } from "./finops-azure-services.helpers";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { PreferencesProvidersState } from "src/app/store/preferences/providers/preferences-providers.state";
import { AzureConfig } from "src/app/store/preferences/providers/preferences-providers.model";
import { FinOpsContextState } from "src/app/store/finops/context/finops-context.state";
import { SelectFinOpsDate } from "src/app/store/finops/context/finops-context.actions";

const FINOPS_AZURE_SERVICES_TOKEN: StateToken<FinOpsAzureServicesStateModel> = new StateToken('finOpsAzureServices');

const FinOpsServicesStateModelDefaults: FinOpsAzureServicesStateModel = {
    historicalDataBySubscriptionByGranularityByDate: {
    },
    datatable: [],
    chartsDataBySubscriptionByDate: {},
    chartsDataDisplayed: {
        filteredHistoricalChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            prediction: false,
            costs: []
        },
        filteredCompareChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            dateString: "",
            prediction: false,
            costs: []
        }
    },
    filteredCounters: {}
}

@State<FinOpsAzureServicesStateModel>({
    name: FINOPS_AZURE_SERVICES_TOKEN,
    defaults: FinOpsServicesStateModelDefaults
})

@Injectable()
export class FinOpsAzureServicesState {

    private readonly KEY = FINOPS_AZURE_SERVICES_TOKEN.getName()

    constructor(
        private FinOpsAzureServicesService: FinOpsAzureServicesService,
        private store: Store,
        private floorPipe: FloorPipe,
        private numberFormatPipe: FormatNumberPipe,
        private dateService: DateService,
        private translate: TranslateService,
        private metaService: MetaService
    ) {
    }

    @Selector([FINOPS_AZURE_SERVICES_TOKEN])
    static getDatatable(state: FinOpsAzureServicesStateModel) {
        return state.datatable
    }

    @Selector([FINOPS_AZURE_SERVICES_TOKEN])
    static getFilteredCounters(state: FinOpsAzureServicesStateModel) {
        return state.filteredCounters
    }

    @Selector([FINOPS_AZURE_SERVICES_TOKEN])
    static getAllChartsData(state: FinOpsAzureServicesStateModel) {
        return state.chartsDataBySubscriptionByDate
    }

    @Selector([FINOPS_AZURE_SERVICES_TOKEN])
    static getChartsDataBySubscription(state: FinOpsAzureServicesStateModel) {
        return (subscription: string) => state.chartsDataBySubscriptionByDate[subscription]
    }

    @Selector([FINOPS_AZURE_SERVICES_TOKEN])
    static getChartsDataBySubscriptionAndDate(state: FinOpsAzureServicesStateModel) {
        return (subscription: string, date: string) => state.chartsDataBySubscriptionByDate[subscription][date]
    }

    @Selector([FINOPS_AZURE_SERVICES_TOKEN])
    static getChartsDataDisplayed(state: FinOpsAzureServicesStateModel) {
        return state.chartsDataDisplayed
    }

    @Selector([FINOPS_AZURE_SERVICES_TOKEN])
    static getFilteredHistoricalChartData(state: FinOpsAzureServicesStateModel) {
        return state.chartsDataDisplayed.filteredHistoricalChartData
    }

    @Selector([FINOPS_AZURE_SERVICES_TOKEN])
    static getFilteredCompareChartData(state: FinOpsAzureServicesStateModel) {
        return state.chartsDataDisplayed.filteredCompareChartData
    }

    @Selector([FINOPS_AZURE_SERVICES_TOKEN])
    static getHistoricalDataBySubscriptionByGranularityByDate(state: FinOpsAzureServicesStateModel) {
        return (subscription:string, granularity: string, date: string) => state.historicalDataBySubscriptionByGranularityByDate[subscription][granularity][date]
    }


    @Action(LoadFinOpsAzureServicesHistoricalData)
    LoadFinOpsAzureServicesHistoricalData(ctx: StateContext<FinOpsAzureServicesStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const azureConfig: AzureConfig = this.store.selectSnapshot(PreferencesProvidersState.getAzureConfig)
        const historicalDates: HistoricalDate[] = this.dateService.getFinopsHistoricalDates(finopsConfig)
        const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)
        let wrapRequestList = []
        let subscriptions: string[] = []

        azureConfig.subscriptions.forEach(subscription => {
            subscriptions.push(subscription.subscription_id)
        });

        for (let subscription of subscriptions) {
            for (let historicalDate of historicalDates) {
                const FINOPS_SUBSCRIPTION_DATE_KEY = this.KEY 
                + "-subscription-"
                + subscription
                + "-granularity-"
                + finopsConfig.granularity
                + "-date-"
                + historicalDate.date;

                if (!this.metaService.isStale(FINOPS_SUBSCRIPTION_DATE_KEY)) {
                    if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_SUBSCRIPTION_DATE_KEY + " still valid, no request executed.")
                    }
                    continue
                }
                wrapRequestList.push(
                    MetaRequestHelper.wrapRequest(
                        this.store,
                        FINOPS_SUBSCRIPTION_DATE_KEY,
                        this.FinOpsAzureServicesService.loadFinOpsAzureServicesHistoricalData(finopsConfig.granularity, subscription, historicalDate.date),
                        resources => {
                            ctx.setState(state => {
                                const h = { ...state.historicalDataBySubscriptionByGranularityByDate };
                                h[subscription] = { ...(h[subscription] || {}) };
                                h[subscription][finopsConfig.granularity] = {
                                    ...(h[subscription][finopsConfig.granularity] || {}),
                                    [historicalDate.date]: resources.measures
                                };
                                return {
                                    ...state,
                                    historicalDataBySubscriptionByGranularityByDate: h
                                };
                            });
                        }
                    )
                )
            }
        }

        return forkJoin(wrapRequestList).pipe(
            finalize( () => {
                    ctx.dispatch(
                        new LoadDataTableAllSubscriptionsFinOpsAzureServices()
                    )
                }
            )
        )
    }

    @Action(LoadDataTableAllSubscriptionsFinOpsAzureServices)
    loadDataTableAllSubscriptionsFinOpsAzureServices(ctx: StateContext<FinOpsAzureServicesStateModel>) {
            const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
            const azureConfig: AzureConfig = this.store.selectSnapshot(PreferencesProvidersState.getAzureConfig)
            const granularity = finopsConfig.granularity as DateGranularityEnum
            const historicalDatesSelected = this.dateService.getFinopsHistoricalDates(finopsConfig).map(item => item.date)
            const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AZURE_SERVICES]]
            const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)
    
            // If cannot find selected date in historical dates, select the last one and load data for it
            if (selectedDateTile === undefined || !historicalDatesSelected.includes(selectedDateTile)) {
                ctx.dispatch([
                    new SelectFinOpsDate(historicalDatesSelected[historicalDatesSelected.length - 1]),
                    new LoadDataTableAllSubscriptionsFinOpsAzureServices()
                ])
                return
            }

            if (isDevMode()) {
                console.warn("Loading all subscriptions data for granularity " + granularity + " and date " + selectedDateTile)
            }

            let total: Record<string, DataForDatatableCompute> = {}
            let grandTotal: DataForDatatableCompute = {
                filteredCounters: {},
                measuresDatatable: {},
                previousTotalCounters: {}
            }

            // // Iterate over all subscriptions
            for(let subscription of azureConfig.subscriptions) {
                const historicalDataByGranularity = ctx.getState().historicalDataBySubscriptionByGranularityByDate[subscription.subscription_id][granularity]
                const dataForDatatable = FinOpsAzureServicesHelper.computeDatatable(historicalDatesSelected, historicalDataByGranularity, filters)
                total = {
                    ...total,
                    [subscription.subscription_id]: dataForDatatable
                }
            }

            grandTotal = FinOpsAzureServicesHelper.computeDatatableGrandTotal(total)

            ctx.patchState(
                {
                    datatable: grandTotal.measuresDatatable[selectedDateTile],
                    filteredCounters: grandTotal.filteredCounters
                }
            )
    }

    @Action(LoadFinOpsAzureServicesChartsData)
    loadFinOpsAzureServicesChartsData(ctx: StateContext<FinOpsAzureServicesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const azureConfig: AzureConfig = this.store.selectSnapshot(PreferencesProvidersState.getAzureConfig)
        const historicalDates: HistoricalDate[] = this.dateService.getChartsHistoricalDates(chartsConfig)
        let wrapRequestList = []
        let subscriptions: string[] = []

        azureConfig.subscriptions.forEach(subscription => {
            subscriptions.push(subscription.subscription_id)
        });

        for(let _subscription of subscriptions) {
            for (let historicalDate of historicalDates) {
                const FINOPS_SERVICE_CHARTS_DATE_KEY = this.KEY
                + '-charts-'
                + 'subscription-' + _subscription
                + '-date-' + historicalDate.date
    
                if(!this.metaService.isStale(FINOPS_SERVICE_CHARTS_DATE_KEY)){
                    if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_SERVICE_CHARTS_DATE_KEY + " still valid, no request executed.")
                    }
                    continue
                }
    
                wrapRequestList.push(
                    MetaRequestHelper.wrapRequest(
                        this.store,
                        FINOPS_SERVICE_CHARTS_DATE_KEY,
                        this.FinOpsAzureServicesService.loadFinOpsAzureServicesMeasures(historicalDate.date, _subscription),
                        (chart_resources) => {
                            ctx.setState(state => {
                                const shallowCloneAllChartsData = {...state.chartsDataBySubscriptionByDate};
                                shallowCloneAllChartsData[_subscription] = { ...(shallowCloneAllChartsData[_subscription] || {})}
                                shallowCloneAllChartsData[_subscription][historicalDate.date] = chart_resources.measures
                                return {
                                    ...state,
                                    chartsDataBySubscriptionByDate: shallowCloneAllChartsData
                                }
                            })
                        }
                    )
                )
            }
        }

        return forkJoin(wrapRequestList).pipe(
            finalize(() => {
                ctx.dispatch(new LoadFinOpsAzureServicesAllSubscriptionsChartsFilteredData())
            })
        )
    }

    @Action(LoadFinOpsAzureServicesAllSubscriptionsChartsFilteredData)
    loadFinOpsAzureServicesAllSubscriptionsChartsFilteredData(ctx: StateContext<FinOpsAzureServicesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const azureConfig: AzureConfig = this.store.selectSnapshot(PreferencesProvidersState.getAzureConfig)
        
        // All chartsData for all subscriptions and for all retrieved dates.
        const bulkChartsData = ctx.getState().chartsDataBySubscriptionByDate

        // Retrieve granularity 
        const granularity = chartsConfig.granularity

        // Retrieve filters to adjust costs depending on selected filters
        const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AZURE_SERVICES_CHARTS]]

        // Get all date to retrieve data from
        const historicalDatesSelected = this.dateService.getChartsHistoricalDates(chartsConfig).map(item => item.date)

        // Get date for compare Chart
        const computeChartDataLastDate = historicalDatesSelected[historicalDatesSelected.length -1]

        const aggregatedCompareData = FinOpsAzureServicesHelper.computeAggregatedCompareChartData(
            azureConfig,
            bulkChartsData,
            computeChartDataLastDate,
            granularity,
            filters,
            this.floorPipe,
            this.numberFormatPipe,
            this.dateService
        );

        const aggregatedHistoricalData =
            FinOpsAzureServicesHelper.computeAggregatedHistoricalChartData(
            azureConfig,
            historicalDatesSelected,
            bulkChartsData,
            filters,
            granularity,
            this.floorPipe,
            this.translate,
            this.dateService
        );

        ctx.patchState({
            chartsDataDisplayed: {
                filteredCompareChartData: aggregatedCompareData,
                filteredHistoricalChartData: aggregatedHistoricalData,
            },
        })
    }
}
